import React, { useMemo } from 'react';

export interface BarDataPoint {
  label: string;
  value: number;
  color?: string;
}

interface BarChartProps {
  data: BarDataPoint[];
  title?: string;
  height?: number;
  showValues?: boolean;
  horizontal?: boolean;
  colorScheme?: 'blue' | 'green' | 'purple' | 'amber' | 'multi';
}

const BarChart: React.FC<BarChartProps> = ({
  data,
  title,
  height = 300,
  showValues = true,
  horizontal = false,
  colorScheme = 'blue'
}) => {
  const colors = useMemo(() => {
    const schemes = {
      blue: ['#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe'],
      green: ['#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5'],
      purple: ['#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe'],
      amber: ['#f59e0b', '#fbbf24', '#fcd34d', '#fde68a', '#fef3c7'],
      multi: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']
    };
    return schemes[colorScheme];
  }, [colorScheme]);

  const chartData = useMemo(() => {
    if (data.length === 0) return null;

    const values = data.map(d => d.value);
    const max = Math.max(...values, 1);

    const padding = { top: 20, right: 40, bottom: 60, left: 60 };
    const chartWidth = 800;
    const chartHeight = height - padding.top - padding.bottom;
    const chartWidthInner = chartWidth - padding.left - padding.right;

    if (horizontal) {
      const barHeight = (chartHeight / data.length) * 0.7;
      const barGap = (chartHeight / data.length) * 0.3;

      const bars = data.map((d, i) => ({
        x: padding.left,
        y: padding.top + (i * chartHeight) / data.length + barGap / 2,
        width: (d.value / max) * chartWidthInner,
        height: barHeight,
        value: d.value,
        label: d.label,
        color: d.color || colors[i % colors.length]
      }));

      return { bars, max, chartWidth, padding, horizontal: true };
    } else {
      const barWidth = (chartWidthInner / data.length) * 0.7;
      const barGap = (chartWidthInner / data.length) * 0.3;

      const bars = data.map((d, i) => ({
        x: padding.left + (i * chartWidthInner) / data.length + barGap / 2,
        y: padding.top + chartHeight - (d.value / max) * chartHeight,
        width: barWidth,
        height: (d.value / max) * chartHeight,
        value: d.value,
        label: d.label,
        color: d.color || colors[i % colors.length]
      }));

      return { bars, max, chartWidth, padding, horizontal: false };
    }
  }, [data, height, horizontal, colors]);

  if (!chartData || data.length === 0) {
    return (
      <div className="flex items-center justify-center bg-gray-50 rounded-lg" style={{ height }}>
        <p className="text-sm text-gray-500">No data available</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {title && <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>}
      <svg
        viewBox={`0 0 ${chartData.chartWidth} ${height}`}
        className="w-full"
        style={{ height: `${height}px` }}
      >
        {/* Bars */}
        {chartData.bars.map((bar, i) => (
          <g key={i}>
            <rect
              x={bar.x}
              y={bar.y}
              width={bar.width}
              height={bar.height}
              fill={bar.color}
              className="transition-all hover:opacity-80"
              rx="4"
            />
            {showValues && (
              <text
                x={chartData.horizontal ? bar.x + bar.width + 5 : bar.x + bar.width / 2}
                y={chartData.horizontal ? bar.y + bar.height / 2 : bar.y - 5}
                textAnchor={chartData.horizontal ? 'start' : 'middle'}
                alignmentBaseline={chartData.horizontal ? 'middle' : 'baseline'}
                className="text-xs font-semibold fill-gray-700"
              >
                {bar.value}
              </text>
            )}
            <title>{`${bar.label}: ${bar.value}`}</title>
          </g>
        ))}

        {/* X-axis labels (vertical mode) */}
        {!chartData.horizontal && chartData.bars.map((bar, i) => (
          <text
            key={i}
            x={bar.x + bar.width / 2}
            y={height - 20}
            textAnchor="middle"
            className="text-xs fill-gray-600"
          >
            {bar.label.length > 12 ? bar.label.substring(0, 12) + '...' : bar.label}
          </text>
        ))}

        {/* Y-axis labels (horizontal mode) */}
        {chartData.horizontal && chartData.bars.map((bar, i) => (
          <text
            key={i}
            x={chartData.padding.left - 10}
            y={bar.y + bar.height / 2}
            textAnchor="end"
            alignmentBaseline="middle"
            className="text-xs fill-gray-600"
          >
            {bar.label.length > 15 ? bar.label.substring(0, 15) + '...' : bar.label}
          </text>
        ))}

        {/* Axes */}
        {!chartData.horizontal && (
          <>
            <line
              x1={chartData.padding.left}
              y1={chartData.padding.top}
              x2={chartData.padding.left}
              y2={height - chartData.padding.bottom}
              stroke="#9ca3af"
              strokeWidth="1"
            />
            <line
              x1={chartData.padding.left}
              y1={height - chartData.padding.bottom}
              x2={chartData.chartWidth - chartData.padding.right}
              y2={height - chartData.padding.bottom}
              stroke="#9ca3af"
              strokeWidth="1"
            />
          </>
        )}
      </svg>
    </div>
  );
};

export default BarChart;
